import {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {Button, Grid, Typography} from '@mui/material';

interface Props {
  isLoading?: boolean;
  rowCount: number;
  pagination: {
    page: number;
    perPage: number;
    totalPages: number;
    totalCount: number;
    hasNext?: boolean;
    hasPrevious?: boolean;
    onNext?: () => void;
    onPrevious?: () => void;
    onPageChange?: (page: number) => void;
  };
}

export const BasicTablePaginationFooter = ({pagination, rowCount, isLoading}: Props) => {
  const {t} = useTranslation();

  const pageButtons = useMemo(() => {
    const {page, totalPages} = pagination;
    const pages = new Set<number>([1, totalPages, page - 1, page, page + 1]);
    const visiblePages = Array.from(pages).filter((currentPage) => currentPage > 0 && currentPage <= totalPages);

    return visiblePages.sort((a, b) => a - b);
  }, [pagination]);

  const shownFrom = rowCount ? (pagination.page - 1) * pagination.perPage + 1 : 0;
  const shownTo = rowCount ? shownFrom + rowCount - 1 : 0;
  const shownCount = rowCount ? Math.min(shownTo, pagination.totalCount) : 0;
  const shownCountText = t('shared.pagination.shownCount', {
    shown: shownCount,
    total: pagination.totalCount,
  });

  return (
    <Grid container justifyContent='space-between' alignItems='center' wrap='nowrap' sx={{marginTop: '-8px'}}>
      <Typography sx={{fontSize: '14px', lineHeight: '21px', color: 'black', whiteSpace: 'nowrap'}}>
        {shownCountText}
      </Typography>
      <Grid container justifyContent='flex-end' alignItems='center' gap='8px' wrap='nowrap'>
        <Button
          variant='outlined'
          onClick={pagination.onPrevious}
          disabled={isLoading || !pagination.hasPrevious}
          sx={{
            textTransform: 'none',
            minWidth: '86px',
            height: '28px',
            borderRadius: '5px',
            borderColor: '#163438',
            color: 'black',
            fontSize: '14px',
            lineHeight: '21px',
            fontWeight: 400,
            padding: '3px 11px',
          }}
        >
          {`\u2190 ${t('shared.formButtons.previous')}`}
        </Button>
        {pageButtons.map((currentPage, index) => (
          <Grid key={currentPage} display='flex' alignItems='center' gap='8px'>
            {index > 0 && currentPage - pageButtons[index - 1] > 1 && (
              <Typography sx={{fontSize: '14px', lineHeight: '21px', color: 'black'}}>...</Typography>
            )}
            <Button
              variant='outlined'
              onClick={() => pagination.onPageChange?.(currentPage)}
              disabled={isLoading || pagination.page === currentPage}
              sx={{
                minWidth: '28px',
                width: '28px',
                height: '28px',
                borderRadius: '5px',
                borderColor: '#163438',
                color: pagination.page === currentPage ? '#163438' : 'black',
                fontSize: '14px',
                lineHeight: '21px',
                fontWeight: 400,
                padding: 0,
                backgroundColor: pagination.page === currentPage ? '#f2efed' : 'white',
                '&.Mui-disabled': {
                  borderColor: '#163438',
                  color: '#163438',
                  backgroundColor: '#f2efed',
                },
              }}
            >
              {currentPage}
            </Button>
          </Grid>
        ))}
        <Button
          variant='outlined'
          onClick={pagination.onNext}
          disabled={isLoading || !pagination.hasNext}
          sx={{
            textTransform: 'none',
            minWidth: '76px',
            height: '28px',
            borderRadius: '5px',
            borderColor: '#163438',
            color: 'black',
            fontSize: '14px',
            lineHeight: '21px',
            fontWeight: 400,
            padding: '3px 11px',
          }}
        >
          {`${t('shared.formButtons.next')} \u2192`}
        </Button>
      </Grid>
    </Grid>
  );
};
